'use client';

import { c as _c } from "react/compiler-runtime";
import { CAPABILITY_OPTIONS, SPECIALISATION_OPTIONS, CHANNEL_EXPERTISE_OPTIONS } from '@sizzlenz/shared';
import { useCountries } from '@/lib/hooks/useCountries';
import { buildDirectoryHref, type DirectoryFilterParam } from '@/lib/directory-filters';
import Link from 'next/link';
import { Wrench, Award, Store, Globe, GraduationCap } from 'lucide-react';
import { useMemo } from 'react';
type OptionDef = readonly {
  id: string;
  label: string;
}[];
interface ResolvedTag {
  id: string;
  label: string;
}
function resolveTags(keys: string[] | undefined, options: OptionDef): ResolvedTag[] {
  if (!keys?.length) return [];
  const map = new Map(options.map(o => [o.id, o.label]));
  return keys.map(k => ({
    id: k,
    label: map.get(k) || k
  }));
}
interface TagRowProps {
  icon: React.ReactNode;
  label: string;
  values: ResolvedTag[];
  /**
   * Directory filter param these chips link to. When set, each chip becomes a
   * link to the directory pre-filtered by that value's id.
   */
  filterParam?: DirectoryFilterParam;
}
function TagRow(t0) {
  const $ = _c(17);
  const {
    icon,
    label,
    values,
    filterParam
  } = t0;
  if (!values.length) {
    return null;
  }
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="text-muted-foreground shrink-0">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-sm text-muted-foreground">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center gap-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== filterParam || $[8] !== values) {
    let t5;
    if ($[10] !== filterParam) {
      t5 = v => filterParam ? <Link key={v.id} href={buildDirectoryHref(filterParam, v.id)} className="inline-block rounded-full bg-secondary px-3 py-1 text-sm text-secondary-foreground cursor-pointer transition-colors hover:bg-secondary/70">{v.label}</Link> : <span key={v.id} className="inline-block rounded-full bg-secondary px-3 py-1 text-sm text-secondary-foreground">{v.label}</span>;
      $[10] = filterParam;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t4 = values.map(t5);
    $[7] = filterParam;
    $[8] = values;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <div className="flex flex-wrap gap-1.5 mt-1 ml-5.5">{t4}</div>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t5) {
    t6 = <div>{t3}{t5}</div>;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
interface EntityProfileSnapshotProps {
  data: Record<string, unknown>;
  className?: string;
}
export function EntityProfileSnapshot(t0) {
  const $ = _c(45);
  const {
    data,
    className
  } = t0;
  const {
    data: t1
  } = useCountries();
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const countries = t2;
  const t3 = data?.capabilities as string[] | undefined;
  let t4;
  if ($[2] !== t3) {
    t4 = resolveTags(t3, CAPABILITY_OPTIONS);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const capabilities = t4;
  const t5 = data?.specialisations as string[] | undefined;
  let t6;
  if ($[4] !== t5) {
    t6 = resolveTags(t5, SPECIALISATION_OPTIONS);
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const specialisations = t6;
  const t7 = data?.channelExpertise as string[] | undefined;
  let t8;
  if ($[6] !== t7) {
    t8 = resolveTags(t7, CHANNEL_EXPERTISE_OPTIONS);
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const channelExpertise = t8;
  let map;
  if ($[8] !== countries) {
    map = new Map();
    countries.forEach(c => map.set(c.alpha3, `${c.emoji} ${c.name}`));
    $[8] = countries;
    $[9] = map;
  } else {
    map = $[9];
  }
  const countryMap = map;
  let t9;
  if ($[10] !== countryMap || $[11] !== data?.geoPresence) {
    t9 = () => {
      const keys = data?.geoPresence as string[] | undefined;
      if (!keys?.length) {
        return [];
      }
      return keys.map(k => ({
        id: k,
        label: countryMap.get(k) || k
      }));
    };
    $[10] = countryMap;
    $[11] = data?.geoPresence;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  data?.geoPresence;
  let t10;
  if ($[13] !== t9) {
    t10 = t9();
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const geoPresence = t10;
  data?.geoExpertise;
  let t11;
  bb0: {
    const keys_0 = data?.geoExpertise as string[] | undefined;
    if (!keys_0?.length) {
      let t12;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = [];
        $[15] = t12;
      } else {
        t12 = $[15];
      }
      t11 = t12;
      break bb0;
    }
    let t12;
    if ($[16] !== countryMap || $[17] !== keys_0) {
      let t13;
      if ($[19] !== countryMap) {
        t13 = k_0 => ({
          id: k_0,
          label: countryMap.get(k_0) || k_0
        });
        $[19] = countryMap;
        $[20] = t13;
      } else {
        t13 = $[20];
      }
      t12 = keys_0.map(t13);
      $[16] = countryMap;
      $[17] = keys_0;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    t11 = t12;
  }
  const geoExpertise = t11;
  const hasAnyData = capabilities.length > 0 || specialisations.length > 0 || channelExpertise.length > 0 || geoPresence.length > 0 || geoExpertise.length > 0;
  if (!hasAnyData) {
    return null;
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Wrench className="w-3.5 h-3.5" />;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== capabilities) {
    t13 = <TagRow icon={t12} label="Capabilities" values={capabilities} filterParam="capability" />;
    $[22] = capabilities;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Award className="w-3.5 h-3.5" />;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== specialisations) {
    t15 = <TagRow icon={t14} label="Specialisations" values={specialisations} filterParam="specialisation" />;
    $[25] = specialisations;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Store className="w-3.5 h-3.5" />;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== channelExpertise) {
    t17 = <TagRow icon={t16} label="Channel Expertise" values={channelExpertise} filterParam="channelExpertise" />;
    $[28] = channelExpertise;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Globe className="w-3.5 h-3.5" />;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== geoPresence) {
    t19 = <TagRow icon={t18} label="Country Presence" values={geoPresence} filterParam="geoPresence" />;
    $[31] = geoPresence;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <GraduationCap className="w-3.5 h-3.5" />;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== geoExpertise) {
    t21 = <TagRow icon={t20} label="Country Expertise" values={geoExpertise} filterParam="geoExpertise" />;
    $[34] = geoExpertise;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== t13 || $[37] !== t15 || $[38] !== t17 || $[39] !== t19 || $[40] !== t21) {
    t22 = <div className="bg-gray-50 p-4 rounded-xl border border-gray-100"><div className="space-y-3">{t13}{t15}{t17}{t19}{t21}</div></div>;
    $[36] = t13;
    $[37] = t15;
    $[38] = t17;
    $[39] = t19;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== className || $[43] !== t22) {
    t23 = <div className={className}>{t22}</div>;
    $[42] = className;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  return t23;
}